import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../Utils/Axios";
import { useLanguage } from "../../context/LanguageContext";

const fallbackFooter = {
  aboutTitle: "About Hospital",
  aboutLinks: [
    { label: "About Us", href: "/about" },
    { label: "Contact Us", href: "/contact" },
    { label: "Terms & Conditions", href: "#" },
    { label: "Privacy Policy", href: "#" },
  ],
  candidateTitle: "For Candidates",
  candidateLinks: [
    { label: "Find Jobs", href: "/find-job" },
    { label: "Job Listing", href: "/job-listing" },
    { label: "Saved Jobs", href: "/bookmark" },
  ],
  hospitalTitle: "For Hospitals",
  hospitalLinks: [
    { label: "Post a Job", href: "/post-job" },
    { label: "Hospital Dashboard", href: "/hospital-dashboard" },
    { label: "Hospital Jobs", href: "/hospital-jobs" },
  ],
  supportLine: "+1 (410) 555-1212",
  socialTitle: "Follow Us",
  socials: {
    facebook: "https://www.facebook.com/",
    twitter: "https://twitter.com/",
    linkedin: "https://www.linkedin.com/",
    instagram: "https://www.instagram.com/",
  },
  copyrightText: "Copyright 2026 Health Professional Workers Pool",
  locationText: "Maryland, USA",
};

const renderNavLink = (item) => {
  if (!item?.href || item.href.startsWith("#")) {
    return (
      <a href={item?.href || "#"}>
        {item?.label || "Link"} <i className="bx bx-up-arrow-alt" />
      </a>
    );
  }
  return (
    <Link to={item.href}>
      {item.label} <i className="bx bx-up-arrow-alt" />
    </Link>
  );
};

function Footer() {
  const [footer, setFooter] = useState(fallbackFooter);
  const { t } = useLanguage();

  useEffect(() => {
    const loadFooter = async () => {
      try {
        const res = await api.get("/api/settings/footer");
        setFooter((prev) => ({ ...prev, ...(res.data?.footer || {}) }));
      } catch (error) {
        setFooter(fallbackFooter);
      }
    };
    loadFooter();
  }, []);

  return (
    <footer className="footer1">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-3 col-md-4 col-sm-6 mb--50 d-flex justify-content-sm-start justify-content-center">
            <div className="footer-widget">
              <div className="widget-title">
                <h5>{footer.aboutTitle || "About Hospital"}</h5>
              </div>
              <div className="menu-container">
                <ul>{(footer.aboutLinks || []).map((item, idx) => <li key={`about-${idx}`}>{renderNavLink(item)}</li>)}</ul>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6 mb--50 d-flex justify-content-md-center justify-content-sm-end justify-content-center">
            <div className="footer-widget">
              <div className="widget-title">
                <h5>{footer.candidateTitle || "For Candidates"}</h5>
              </div>
              <div className="menu-container">
                <ul>
                  {(footer.candidateLinks || []).map((item, idx) => (
                    <li key={`candidate-${idx}`}>{renderNavLink(item)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6 mb--50 d-flex justify-content-lg-center justify-content-md-end justify-content-sm-start justify-content-center">
            <div className="footer-widget">
              <div className="widget-title">
                <h5>{footer.hospitalTitle || "For Hospitals"}</h5>
              </div>
              <div className="menu-container">
                <ul>
                  {(footer.hospitalLinks || []).map((item, idx) => (
                    <li key={`hospital-${idx}`}>{renderNavLink(item)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-4 col-sm-6 d-flex justify-content-lg-end justify-content-md-center justify-content-sm-end justify-content-center">
            <div className="footer-widget four">
              <div className="widget-title">
                <h5>{t("downloadApp")}</h5>
              </div>
              <div className="app-list">
                <ul>
                  <li>
                    <a href="#">
                      <img src="/assets/images/icon/apple-app.svg" alt="" />
                    </a>
                  </li>
                  <li>
                    <a href="#">
                      <img src="/assets/images/icon/google-play.svg" alt="" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-btm">
          <div className="footer-logo">
            <img
              src="/assets/images/hpwp-logo.jpg"
              alt="Health Professional Workers Pool"
              style={{ maxHeight: 72, width: 260, objectFit: "contain" }}
            />
          </div>
          <div className="row align-items-center">
            <div className="col-lg-6 d-flex justify-content-lg-start justify-content-center">
              <div className="support">
                <div className="icon">
                  <img src="/assets/images/icon/footer-support-icon.svg" alt="" />
                </div>
                <div className="content">
                  <h5>{t("supportLine")}:</h5>
                  <a href={`tel:${(footer.supportLine || "").replace(/\s+/g, "")}`}>{footer.supportLine}</a>
                </div>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end justify-content-center">
              <div className="footer-btm-menu">
                <ul>
                  <li>
                    <a href="#">{footer.locationText || "Maryland, USA"}</a>
                  </li>
                  <li>
                    <a href="#">{t("termsOfService")}</a>
                  </li>
                  <li>
                    <a href="#">{t("sitemap")}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row border-top align-items-center">
            <div className="col-lg-6 d-flex  justify-content-lg-start justify-content-center">
              <div className="copyright-area">
                <p>{footer.copyrightText || "Copyright 2026 Health Professional Workers Pool"}</p>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end justify-content-center">
              <div className="social-area">
                <h6>{footer.socialTitle || t("followUs")}:</h6>
                <ul>
                  <li>
                    <a href={footer.socials?.facebook || "#"}>
                      <i className="bx bxl-facebook" />
                    </a>
                  </li>
                  <li>
                    <a href={footer.socials?.twitter || "#"}>
                      <i className="bx bxl-twitter" />
                    </a>
                  </li>
                  <li>
                    <a href={footer.socials?.linkedin || "#"}>
                      <i className="bx bxl-linkedin" />
                    </a>
                  </li>
                  <li>
                    <a href={footer.socials?.instagram || "#"}>
                      <i className="bx bxl-instagram" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
